import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

// Componente Button simples
const Button: React.FC<{
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
}> = ({ onClick, children, className = '', disabled = false }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg font-medium transition-colors ${className}`}
    disabled={disabled}
  >
    {children}
  </button>
);

// Tipos para o sistema de testes
interface TestCase {
  id: string;
  name: string;
  description: string;
  suite: string;
  status: 'pending' | 'running' | 'pass' | 'fail';
  duration?: number;
  error?: string;
  check: () => Promise<boolean>;
}

interface TestResult {
  suite: string;
  name: string;
  status: 'pass' | 'fail';
  duration: number;
  timestamp: Date;
  error?: string;
}

interface TestSuite {
  name: string;
  description: string;
  tests: TestCase[];
}

interface NotificationMessage {
  id: string;
  type: 'info' | 'success' | 'error' | 'warning';
  message: string;
  timestamp: Date;
}

// Componente principal do Test Runner
const AutoTestRunner: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState('');
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);
  const [results, setResults] = useState<TestResult[]>([]);
  const [selectedSuite, setSelectedSuite] = useState('all');
  
  // Inicializar variáveis globais para testes de agenda
  const inicializarVariaveisAgenda = () => {
    // Mock data para testes
    (window as any).diasFechadosAgendamento = [
      {
        data: '2025-12-25',
        filial: 'FILIAL_1',
        motivo: 'Feriado - Natal',
        criadoPor: 'Sistema',
        criadoEm: new Date().toISOString()
      }
    ];
    
    (window as any).diasFechadosAgendamentoFrios = [
      {
        data: '2025-12-31',
        filial: 'FILIAL_1',
        motivo: 'Fechamento para FRIOS - Ano Novo',
        criadoPor: 'Sistema',
        criadoEm: new Date().toISOString()
      }
    ];
    
    console.log('✅ Variáveis de agenda inicializadas para testes');
  };

  // Definição das suítes de testes
  const [testSuites] = useState<TestSuite[]>([
    {
      name: 'Autenticação',
      description: 'Testes de login e autenticação',
      tests: [
        {
          id: 'auth-login-usuario-senha',
          name: 'Login Entrando com usuário e senha',
          description: 'Testa login completo com credenciais válidas',
          suite: 'Autenticação',
          status: 'pending',
          check: async () => {
            // Verifica se está na página de login
            if (!window.location.pathname.includes('/login')) {
              throw new Error('Não está na página de login');
            }
            
            // Verifica se formulário de login existe
            const loginForm = document.querySelector('[data-testid="login-form"]');
            if (!loginForm) {
              throw new Error('Formulário de login não encontrado');
            }
            
            // Verifica campos de usuário e senha
            const usuarioInput = document.querySelector('[data-testid="email"]') as HTMLInputElement;
            const senhaInput = document.querySelector('[data-testid="password"]') as HTMLInputElement;
            if (!usuarioInput || !senhaInput) {
              throw new Error('Campos de usuário ou senha não encontrados');
            }
            
            // Verifica botão de login
            const loginButton = document.querySelector('button[type="submit"]') as HTMLButtonElement;
            if (!loginButton) {
              throw new Error('Botão de login não encontrado');
            }
            
            return true;
          }
        },
        {
          id: 'auth-redefinicao-senha',
          name: 'Teste uma redefinição de senha no Esqueci minha senha',
          description: 'Testa fluxo completo de recuperação de senha',
          suite: 'Autenticação',
          status: 'pending',
          check: async () => {
            // Verifica se botão "Esqueci a senha" existe
            const esqueciSenhaBtn = document.querySelector('[data-testid="esqueci-senha"]');
            if (!esqueciSenhaBtn) {
              throw new Error('Botão "Esqueci a senha" não encontrado');
            }
            
            // Clica no botão
            (esqueciSenhaBtn as HTMLElement).click();
            
            // Espera um pouco para o modal abrir
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Verifica se o modal abriu
            const modal = document.querySelector('[data-testid="esqueci-senha-modal"]') || 
                         document.querySelector('div[class*="fixed inset-0"]');
            if (!modal) {
              throw new Error('Modal de recuperação de senha não abriu');
            }
            
            // Verifica campo de email no modal
            const emailInput = document.querySelector('input[placeholder*="email"]') as HTMLInputElement;
            if (!emailInput) {
              throw new Error('Campo de email não encontrado no modal');
            }
            
            // Verifica botão de envio
            const enviarBtn = document.querySelector('button[class*="bg-blue-600"]') as HTMLButtonElement;
            if (!enviarBtn) {
              throw new Error('Botão de envio não encontrado');
            }
            
            // Fecha modal
            const closeButton = document.querySelector('button[aria-label="Close"]') ||
                             document.querySelector('button[class*="close"]');
            if (closeButton) {
              (closeButton as HTMLElement).click();
            }
            
            return true;
          }
        },
        {
          id: 'auth-solicitacao-acesso',
          name: 'Teste um solicite seu acesso e envie a solicitação',
          description: 'Testa fluxo de solicitação de acesso',
          suite: 'Autenticação',
          status: 'pending',
          check: async () => {
            // Verifica se está na página de login
            if (!window.location.pathname.includes('/login')) {
              throw new Error('Não está na página de login');
            }
            
            // Preenche email que não existe
            const usuarioInput = document.querySelector('[data-testid="email"]') as HTMLInputElement;
            if (!usuarioInput) {
              throw new Error('Campo de usuário não encontrado');
            }
            
            usuarioInput.value = 'naoexiste@teste.com';
            
            // Preenche senha
            const senhaInput = document.querySelector('[data-testid="password"]') as HTMLInputElement;
            if (!senhaInput) {
              throw new Error('Campo de senha não encontrado');
            }
            
            senhaInput.value = 'senha123';
            
            // Limpa campos
            usuarioInput.value = '';
            senhaInput.value = '';
            
            return true;
          }
        }
      ]
    },
    {
      name: 'Agenda',
      description: 'Testes de fechamento e reabertura de dias na agenda',
      tests: [
        {
          id: 'agenda-fechar-dia',
          name: 'Fechar dia na agenda',
          description: 'Testa fechar um dia para agendamentos gerais',
          suite: 'Agenda',
          status: 'pending',
          check: async () => {
            // Verifica se as funções globais existem
            if (!(window as any).diasFechadosAgendamento) {
              throw new Error('Função diasFechadosAgendamento não encontrada');
            }
            
            // Data de teste (amanhã)
            const amanha = new Date();
            amanha.setDate(amanha.getDate() + 1);
            const dataTeste = amanha.toISOString().split('T')[0];
            
            // Verifica se o dia já está fechado
            const diaJaFechado = (window as any).diasFechadosAgendamento.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (diaJaFechado) {
              // Se já está fechado, testa reabrir primeiro
              (window as any).diasFechadosAgendamento = (window as any).diasFechadosAgendamento.filter((d: any) => 
                !(d && d.data === dataTeste)
              );
            }
            
            // Fecha o dia
            (window as any).diasFechadosAgendamento.push({
              data: dataTeste,
              filial: 'FILIAL_1',
              motivo: 'Teste automatizado',
              criadoPor: 'Test Runner',
              criadoEm: new Date().toISOString()
            });
            
            // Verifica se foi fechado
            const diaFechado = (window as any).diasFechadosAgendamento.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (!diaFechado) {
              throw new Error('Dia não foi fechado corretamente');
            }
            
            return true;
          }
        },
        {
          id: 'agenda-reabrir-dia',
          name: 'Reabrir dia na agenda',
          description: 'Testa reabrir um dia fechado para agendamentos gerais',
          suite: 'Agenda',
          status: 'pending',
          check: async () => {
            // Verifica se as funções globais existem
            if (!(window as any).diasFechadosAgendamento) {
              throw new Error('Função diasFechadosAgendamento não encontrada');
            }
            
            // Data de teste (amanhã)
            const amanha = new Date();
            amanha.setDate(amanha.getDate() + 1);
            const dataTeste = amanha.toISOString().split('T')[0];
            
            // Verifica se o dia está fechado
            const diaJaFechado = (window as any).diasFechadosAgendamento.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (!diaJaFechado) {
              // Se não está fechado, fecha primeiro
              (window as any).diasFechadosAgendamento.push({
                data: dataTeste,
                filial: 'FILIAL_1',
                motivo: 'Teste automatizado',
                criadoPor: 'Test Runner',
                criadoEm: new Date().toISOString()
              });
            }
            
            // Reabre o dia (remove da lista)
            (window as any).diasFechadosAgendamento = (window as any).diasFechadosAgendamento.filter((d: any) => 
              !(d && d.data === dataTeste)
            );
            
            // Verifica se foi reaberto
            const diaAindaFechado = (window as any).diasFechadosAgendamento.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (diaAindaFechado) {
              throw new Error('Dia não foi reaberto corretamente');
            }
            
            return true;
          }
        },
        {
          id: 'agenda-fechar-frios',
          name: 'Fechar dia para FRIOS',
          description: 'Testa fechar um dia especificamente para produtos FRIOS',
          suite: 'Agenda',
          status: 'pending',
          check: async () => {
            // Verifica se as funções globais existem
            if (!(window as any).diasFechadosAgendamentoFrios) {
              throw new Error('Função diasFechadosAgendamentoFrios não encontrada');
            }
            
            // Data de teste (amanhã)
            const amanha = new Date();
            amanha.setDate(amanha.getDate() + 1);
            const dataTeste = amanha.toISOString().split('T')[0];
            
            // Verifica se o dia já está fechado para frios
            const diaJaFechado = (window as any).diasFechadosAgendamentoFrios.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (diaJaFechado) {
              // Se já está fechado, reabre primeiro
              (window as any).diasFechadosAgendamentoFrios = (window as any).diasFechadosAgendamentoFrios.filter((d: any) => 
                !(d && d.data === dataTeste)
              );
            }
            
            // Fecha o dia para frios
            (window as any).diasFechadosAgendamentoFrios.push({
              data: dataTeste,
              filial: 'FILIAL_1',
              motivo: 'Teste automatizado - FRIOS',
              criadoPor: 'Test Runner',
              criadoEm: new Date().toISOString()
            });
            
            // Verifica se foi fechado
            const diaFechado = (window as any).diasFechadosAgendamentoFrios.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (!diaFechado) {
              throw new Error('Dia não foi fechado para FRIOS corretamente');
            }
            
            return true;
          }
        },
        {
          id: 'agenda-reabrir-frios',
          name: 'Reabrir dia para FRIOS',
          description: 'Testa reabrir um dia fechado especificamente para produtos FRIOS',
          suite: 'Agenda',
          status: 'pending',
          check: async () => {
            // Verifica se as funções globais existem
            if (!(window as any).diasFechadosAgendamentoFrios) {
              throw new Error('Função diasFechadosAgendamentoFrios não encontrada');
            }
            
            // Data de teste (amanhã)
            const amanha = new Date();
            amanha.setDate(amanha.getDate() + 1);
            const dataTeste = amanha.toISOString().split('T')[0];
            
            // Verifica se o dia está fechado para frios
            const diaJaFechado = (window as any).diasFechadosAgendamentoFrios.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (!diaJaFechado) {
              // Se não está fechado, fecha primeiro
              (window as any).diasFechadosAgendamentoFrios.push({
                data: dataTeste,
                filial: 'FILIAL_1',
                motivo: 'Teste automatizado - FRIOS',
                criadoPor: 'Test Runner',
                criadoEm: new Date().toISOString()
              });
            }
            
            // Reabre o dia para frios (remove da lista)
            (window as any).diasFechadosAgendamentoFrios = (window as any).diasFechadosAgendamentoFrios.filter((d: any) => 
              !(d && d.data === dataTeste)
            );
            
            // Verifica se foi reaberto
            const diaAindaFechado = (window as any).diasFechadosAgendamentoFrios.some((d: any) => 
              d && d.data === dataTeste
            );
            
            if (diaAindaFechado) {
              throw new Error('Dia não foi reaberto para FRIOS corretamente');
            }
            
            return true;
          }
        }
      ]
    }
  ]);

  // Sistema de notificação
  const addNotification = (type: 'info' | 'success' | 'error' | 'warning', message: string) => {
    const notification: NotificationMessage = {
      id: Date.now().toString(),
      type,
      message,
      timestamp: new Date()
    };
    
    setNotifications(prev => [notification, ...prev].slice(0, 50)); // Mantém últimas 50
    
    // Remove notificação após 5 segundos
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Adicionar teste dinamicamente
  const addTest = (suiteName: string, testCase: Omit<TestCase, 'status'>) => {
    // Implementação simplificada
    console.log('Teste adicionado:', testCase);
  };

  // Executar um teste específico
  const runTest = async (testCase: TestCase): Promise<TestResult> => {
    const startTime = Date.now();
    setCurrentTest(testCase.name);
    
    try {
      addNotification('info', `Executando: ${testCase.name}`);
      
      const result = await testCase.check();
      const duration = Date.now() - startTime;
      
      if (result) {
        addNotification('success', `✅ ${testCase.name} - PASS`);
        return {
          suite: testCase.suite,
          name: testCase.name,
          status: 'pass',
          duration,
          timestamp: new Date()
        };
      } else {
        throw new Error('Test returned false');
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      addNotification('error', `❌ ${testCase.name} - FAIL: ${errorMessage}`);
      return {
        suite: testCase.suite,
        name: testCase.name,
        status: 'fail',
        duration,
        error: errorMessage,
        timestamp: new Date()
      };
    } finally {
      setCurrentTest('');
    }
  };

  // Função para calcular estatísticas dos testes
  const getTestStats = (results: TestResult[]) => {
    const total = results.length;
    const passed = results.filter(r => r.status === 'pass').length;
    const failed = results.filter(r => r.status === 'fail').length;
    
    return { total, passed, failed };
  };

  // Executar todos os testes
  const runAllTests = async () => {
    setIsRunning(true);
    setResults([]);
    setNotifications([]);
    
    // Inicializar variáveis de agenda antes dos testes
    inicializarVariaveisAgenda();
    
    const allTests = selectedSuite === 'all' 
      ? testSuites.flatMap(suite => suite.tests)
      : testSuites.find(suite => suite.name === selectedSuite)?.tests || [];
    
    const results: TestResult[] = [];
    
    try {
      for (const test of allTests) {
        const result = await runTest(test);
        results.push(result);
        
        // Pequena pausa entre testes
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setResults(results);
      
      // Estatísticas finais
      const stats = getTestStats(results);
      addNotification('info', `Testes concluídos: ${stats.passed} passaram, ${stats.failed} falharam`);
      
    } catch (error) {
      addNotification('error', `Erro ao executar testes: ${error}`);
    } finally {
      setIsRunning(false);
    }
  };

  // Teste rápido
  const runQuickTest = async () => {
    setIsRunning(true);
    addNotification('info', '⚡ Executando teste rápido...');
    
    // Testes básicos
    const quickTests = [
      {
        id: 'quick-1',
        name: 'Verificar estado de autenticação',
        description: 'Verifica se usuário está autenticado',
        suite: 'Autenticação',
        status: 'pending' as const,
        check: async () => {
          const user = localStorage.getItem('user');
          return !!user;
        }
      }
    ];
    
    const results: TestResult[] = [];
    
    for (const test of quickTests) {
      const result = await runTest(test as TestCase);
      results.push(result);
    }
    
    setResults(results);
    setIsRunning(false);
  };

  // Limpar resultados
  const clearResults = () => {
    setResults([]);
    setNotifications([]);
    addNotification('info', '🧹 Resultados limpos');
  };

  // Gerar relatório
  const generateReport = (testResults: TestResult[]) => {
    const report = {
      timestamp: new Date().toISOString(),
      summary: getTestStats(testResults),
      results: testResults
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Expor globalmente para uso no console
  useEffect(() => {
    (window as any).autoTestRunner = {
      runAllTests,
      runQuickTest,
      clearResults,
      addTest,
      addNotification,
      results,
      stats: getTestStats(results)
    };
  }, [runAllTests, runQuickTest, clearResults, addTest, addNotification, results]);

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Test Runner
        </Button>
        
        {/* Notificações flutuantes */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-3 rounded-lg shadow-lg text-white max-w-sm ${
                notification.type === 'success' ? 'bg-green-500' :
                notification.type === 'error' ? 'bg-red-500' :
                notification.type === 'warning' ? 'bg-yellow-500' :
                'bg-blue-500'
              }`}
            >
              {notification.message}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const stats = getTestStats(results);

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">🧪 Test Runner Automático</h2>
        
        {/* Controles */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            onClick={runAllTests}
            disabled={isRunning}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {isRunning ? 'Executando...' : 'Executar Todos'}
          </Button>
          <Button
            onClick={runQuickTest}
            disabled={isRunning}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Teste Rápido
          </Button>
          <Button
            onClick={clearResults}
            disabled={isRunning}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            Limpar
          </Button>
          <Button
            onClick={() => generateReport(results)}
            disabled={results.length === 0}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Relatório
          </Button>
        </div>

        {/* Filtro de suíte */}
        <div className="mb-6">
          <select
            value={selectedSuite}
            onChange={(e) => setSelectedSuite(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">Todas as Suítes</option>
            {testSuites.map(suite => (
              <option key={suite.name} value={suite.name}>{suite.name}</option>
            ))}
          </select>
        </div>

        {/* Status atual */}
        {currentTest && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="text-blue-800 font-medium">Executando: {currentTest}</div>
          </div>
        )}

        {/* Estatísticas */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-800">{stats.passed}</div>
            <div className="text-sm text-green-600">Passaram</div>
          </div>
          <div className="bg-red-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-800">{stats.failed}</div>
            <div className="text-sm text-red-600">Falharam</div>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-800">
              {stats.total > 0 ? Math.round((stats.passed / stats.total) * 100) : 0}%
            </div>
            <div className="text-sm text-blue-600">Sucesso</div>
          </div>
        </div>

        {/* Resultados dos testes */}
        <div className="space-y-4">
          {testSuites.map(suite => (
            <div key={suite.name} className="border border-gray-200 rounded-lg">
              <div className="p-3 bg-gray-50 border-b border-gray-200">
                <h3 className="font-medium text-gray-800">{suite.name}</h3>
                <p className="text-sm text-gray-600">{suite.description}</p>
              </div>
              <div className="p-3">
                {suite.tests.map(test => {
                  const result = results.find(r => r.name === test.name);
                  return (
                    <div key={test.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">{test.name}</div>
                        <div className="text-sm text-gray-600">{test.description}</div>
                        {result && (
                          <div className="text-xs text-gray-500">
                            {result.duration}ms
                            {result.error && <span className="text-red-600 ml-2">- {result.error}</span>}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          result?.status === 'pass' ? 'bg-green-100 text-green-800' :
                          result?.status === 'fail' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {result?.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Instruções */}
        <div className="bg-blue-50 p-3 rounded-lg text-sm mt-6">
          <div className="font-medium text-blue-800 mb-1">Como usar:</div>
          <div className="text-blue-700 space-y-1">
            <div>1. Executar Todos: Roda todos os testes do sistema</div>
            <div>2. Teste Rápido: Verificação básica de funcionalidades</div>
            <div>3. Console: Use window.autoTestRunner.runAllTests()</div>
            <div>4. Adicionar Teste: Use window.autoTestRunner.addTest()</div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AutoTestRunner;
